import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  User,
  Building2,
  Phone,
  Loader2,
  Mail,
  MapPin,
  Clock,
  FileText,
  Banknote,
  CheckCircle,
  LocateFixed,
  Receipt,
  CreditCard,
  Fingerprint,
  Car,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast, Toaster } from "sonner";
import { transformFormDataToBackend, createApiFormData, normalizeIfscCode } from "@/utils/dataTransform";
import { createVendor, validateFormData } from "@/services/vendorApi";
import { useNavigate } from "react-router-dom";


// Enhanced validation schema with better time handling
const vendorFormSchema = z
  .object({
    // Personal Information
    firstName: z.string().min(2, "First name must be at least 2 characters").max(50, "First name too long"),
    lastName: z.string().min(2, "Last name must be at least 2 characters").max(50, "Last name too long"),
    personalEmail: z.string().email("Invalid email format").optional().or(z.literal("")),

    // Business Information
    washCenterName: z.string().min(3, "Wash center name must be at least 3 characters").max(100, "Name too long"),
    businessEmail: z.string().email("Invalid business email format"),
    opensAt: z.string().min(1, "Opening time is required"),
    closesAt: z.string().min(1, "Closing time is required"),
    ownerPhoneNumber: z.string().regex(/^[6-9]\d{9}$/, "Invalid Indian phone number format"),
    washCentrePhoneNumber: z.string().regex(/^[6-9]\d{9}$/, "Invalid Indian phone number format"),

    // Address Information
    street1: z.string().min(5, "Street address must be at least 5 characters"),
    street2: z.string().min(2, "Street 2 is required"),
    city: z.string().min(2, "City is required"),
    state: z.string().min(2, "State is required"),
    pincode: z.string().regex(/^[1-9][0-9]{5}$/, "Invalid pincode format"),
    location: z.string().min(3, "Location must be at least 3 characters"),
    latitude: z.string().optional(),
    longitude: z.string().optional(),

    // Legal Documents
    hasGst: z.boolean().default(true),
    gstNumber: z.string().optional().or(z.literal("")),
    panNumber: z.string().regex(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN number format"),
    aadharNumber: z.string().regex(/^[2-9][0-9]{11}$/, "Invalid Aadhar number format"),
    panFile: z.any().refine(file => file instanceof File, {
      message: "PAN document is required",
    }),
    aadharFile: z.any().refine(file => file instanceof File, {
      message: "Aadhar document is required",
    }),

    // Primary Banking Details
    bankName: z.string().min(2, "Bank name is required"),
    bankAccountNumber: z.string().regex(/^[0-9]{9,18}$/, "Invalid account number"),
    ifscCode: z.string().regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Invalid IFSC code format"),
    accountType: z.enum(["savings", "current", "salary"], {
      errorMap: () => ({ message: "Please select a valid account type" })
    }),
    branchName: z.string().min(2, "Branch name is required"),

    // Secondary Banking Details
    hasSecondaryAccount: z.boolean().default(false),
    secondaryBankName: z.string().optional(),
    secondaryBankAccountNumber: z.string().optional(),
    secondaryIfscCode: z.string().optional(),
    secondaryAccountType: z.enum(["savings", "current", "salary"]).optional(),
    secondaryBranchName: z.string().optional(),
  })
  .refine(
    (data) => {
      // GST validation: If hasGst is true, GST number is required
      if (data.hasGst) {
        return data.gstNumber && data.gstNumber.length === 15 &&
          /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(data.gstNumber);
      }
      return true;
    },
    {
      message: "Valid GST number is required when GST is enabled",
      path: ["gstNumber"],
    }
  )
  .refine(
    (data) => {
      // Secondary account validation
      if (data.hasSecondaryAccount) {
        return (
          data.secondaryBankName &&
          data.secondaryBankAccountNumber &&
          data.secondaryIfscCode &&
          data.secondaryAccountType &&
          data.secondaryBranchName
        );
      }
      return true;
    },
    {
      message: "All secondary bank details are required when secondary account is enabled",
      path: ["secondaryBankName"],
    }
  )
  .refine(
    (data) => {
      // Time validation: closesAt must be after opensAt
      if (data.opensAt && data.closesAt) {
        const openTime = new Date(`2000-01-01T${data.opensAt}`);
        const closeTime = new Date(`2000-01-01T${data.closesAt}`);
        return closeTime > openTime;
      }
      return true;
    },
    {
      message: "Closing time must be after opening time",
      path: ["closesAt"],
    }
  );

const VendorOnboardForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [formProgress, setFormProgress] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const form = useForm<z.infer<typeof vendorFormSchema>>({
    resolver: zodResolver(vendorFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      personalEmail: "",
      washCenterName: "",
      businessEmail: "",
      opensAt: "09:00",
      closesAt: "18:00",
      ownerPhoneNumber: "",
      washCentrePhoneNumber: "",
      street1: "",
      street2: "",
      city: "",
      state: "",
      pincode: "",
      location: "",
      latitude: "",
      longitude: "",
      hasGst: true,
      gstNumber: "",
      panNumber: "",
      aadharNumber: "",
      panFile: undefined,
      aadharFile: undefined,
      bankName: "",
      bankAccountNumber: "",
      ifscCode: "",
      accountType: "savings",
      branchName: "",
      hasSecondaryAccount: false,
      secondaryBankName: "",
      secondaryBankAccountNumber: "",
      secondaryIfscCode: "",
      secondaryAccountType: "savings",
      secondaryBranchName: "",
    },
  });

  const navigate = useNavigate();

  // Track form progress
  useEffect(() => {
    const watchedFields = form.watch();
    const hasSecondaryAccount = watchedFields.hasSecondaryAccount;
    
    // Define required fields based on secondary account toggle
    const requiredFields = [
      'firstName', 'lastName', 'washCenterName', 'businessEmail', 
      'opensAt', 'closesAt', 'ownerPhoneNumber', 'washCentrePhoneNumber',
      'street1', 'street2', 'city', 'state', 'pincode', 'location',
      'latitude', 'longitude', 'panNumber', 'aadharNumber',
      'bankName', 'bankAccountNumber', 'ifscCode', 'accountType', 'branchName'
    ];
    
    // Add GST field if GST is enabled
    if (watchedFields.hasGst) {
      requiredFields.push('gstNumber');
    }
    
    // Add secondary account fields if secondary account is enabled
    if (hasSecondaryAccount) {
      requiredFields.push(
        'secondaryBankName', 'secondaryBankAccountNumber', 
        'secondaryIfscCode', 'secondaryAccountType', 'secondaryBranchName'
      );
    }
    
    const totalFields = requiredFields.length;
    const filledFields = requiredFields.filter((fieldName) => {
      const value = (watchedFields as Record<string, unknown>)[fieldName];
      return value !== '' && value !== undefined && value !== null &&
        (typeof value !== 'boolean' || value === true);
    }).length;

    const progress = Math.round((filledFields / totalFields) * 100);
    setFormProgress(progress);
  }, [form.watch()]);

  const fetchBranchName = async (ifscCode: string) => {
    try {
      const response = await fetch(`https://ifsc.razorpay.com/${ifscCode}`);
      if (!response.ok) throw new Error("Failed to fetch branch details");
      const data = await response.json();
      if (data.BRANCH) {
        form.setValue("branchName", data.BRANCH);
        toast.success("Branch name fetched successfully!");
      } else {
        throw new Error("Branch name not found");
      }
    } catch {
      toast.error("Failed to fetch branch name. Please check the IFSC code.");
    }
  };

  const fetchSecondaryBranchName = async (secondaryIfscCode: string) => {
    try {
      const response = await fetch(`https://ifsc.razorpay.com/${secondaryIfscCode}`);
      if (!response.ok) throw new Error("Failed to fetch branch details");
      const data = await response.json();
      if (data.BRANCH) {
        form.setValue("secondaryBranchName", data.BRANCH);
        toast.success("Branch name fetched successfully!");
      } else {
        throw new Error("Branch name not found");
      }
    } catch {
      toast.error("Failed to fetch branch name. Please check the IFSC code.");
    }
  };

  const getCurrentLocation = async () => {
    setIsLoadingLocation(true);
    try {
      if (!navigator.geolocation) {
        throw new Error("Geolocation is not supported by your browser");
      }

      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0,
          });
      });

      const { latitude, longitude } = position.coords;
      form.setValue("latitude", latitude.toString());
      form.setValue("longitude", longitude.toString());

      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }

      if (data.address) {
        const address = data.address;
        form.setValue("street1", address.road || "");
        form.setValue("street2", address.suburb || "");
        form.setValue("location", address.city || address.town || address.village);
        form.setValue("pincode", address.postcode || "");
        toast.success("Location and address fetched successfully!");
      } else {
        throw new Error("No address found for the given coordinates");
      }
    } catch (error) {
      console.error("Error getting location:", error);
      let errorMessage = "Failed to get location. Please try again or enter manually.";
      if (error instanceof Error) {
        if (error.message.includes("permission")) {
          errorMessage = "Location permission denied. Please enable location access in your browser settings.";
        } else if (error.message.includes("timeout")) {
          errorMessage = "Location request timed out. Please try again.";
        }
      }
      toast.error(errorMessage);
    } finally {
      setIsLoadingLocation(false);
    }
  };

  // Fix: Add computed phoneNumber for backend compatibility
  const getBackendData = (data: any) => {
    const backendData = {
      ...data,
      phoneNumber: data.ownerPhoneNumber, // or choose which to send
    };
    // Remove address
    delete backendData.address;
    // Add country
    backendData.country = "India";
    return backendData;
  };

  const onSubmit = async (data: z.infer<typeof vendorFormSchema>) => {
    console.log("🎯 Form submission started");
    console.log("📝 Form data received:", data);

    setIsSubmitting(true);

    try {
      // Validate form data before submission
      const validationResult = vendorFormSchema.safeParse(data);
      if (!validationResult.success) {
        const errors = validationResult.error.errors;
        const firstError = errors[0];
        toast.error(`${firstError.path.join('.')}: ${firstError.message}`);
        return;
      }

      // Transform form data to backend format
      console.log("🔄 Transforming form data...");
      const transformedData = transformFormDataToBackend(getBackendData(data));
      console.log("✅ Data transformed:", transformedData);

      // Create FormData for API submission
      console.log("📦 Creating FormData for API...");
      const apiFormData = createApiFormData(getBackendData(data), transformedData);

      // Validate FormData before submission
      console.log("🔍 Validating FormData...");
      validateFormData(apiFormData);
      console.log("✅ FormData validation passed");

      // Submit to API with timeout
      console.log("🚀 Submitting to API...");
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await createVendor(apiFormData);
      clearTimeout(timeoutId);

      // Show success message
      console.log("🎉 Submission successful!");
      console.log("✅ Vendor created successfully:", response);

      // Show success modal
      setShowSuccessModal(true);
    } catch (error: any) {
      console.error("💥 Error in form submission:", error);
      const errorMessage =
        error.response?.data?.error?.message ||
        error.message ||
        "An unexpected error occurred. Please try again.";
      toast.error(errorMessage, {
        duration: 5000,
        description: error.response?.status === 503
          ? "Your information is fine; please retry when the service is back online."
          : "Please check your information and try again.",
      });
    } finally {
      setIsSubmitting(false);
      console.log("🏁 Form submission completed");
    }
  };

  return (
    <div
      className="h-screen w-full bg-slate-50 dark:bg-slate-900 grid grid-cols-1 lg:grid-cols-4 overflow-hidden"
      style={{ margin: 0, padding: 0, height: '100vh', width: '100vw', position: 'fixed', top: 0, left: 0 }}
    >
      {/* Left Panel - Timelyyy Details */}
      <div className="hidden lg:block lg:col-span-1 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 p-6 lg:p-8 m-2 lg:m-4 rounded-2xl">
        <div className="flex flex-col h-full gap-1 lg:gap-3">
          <button 
            onClick={() => navigate("/")}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer text-left"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-white/30 to-white/10 rounded-xl flex items-center justify-center shadow-lg">
              <Car className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-white">Timelyyy</h1>
              <p className="text-xs text-blue-100">Vehicle Wash Booking</p>
            </div>
          </button>
          <h2 className="text-2xl lg:text-3xl font-bold text-white">
            Join India's Leading Vehicle Wash Platform
          </h2>
          <p className="text-blue-100 text-base lg:text-sm">
            Connect with thousands of customers looking for reliable car wash services.
            Start your journey with us and grow your business.
          </p>

          {/* Chat Support */}
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              Chat with us
            </h3>
            <div className="flex flex-col gap-2">
              <button className="flex items-center gap-2 text-blue-100 hover:text-white transition-colors text-sm">
                <div className="size-6 bg-white/20 rounded-full flex items-center justify-center">
                  <Phone className="size-3" />
                </div>
                Start a live chat
              </button>
              <button className="flex items-center gap-2 text-blue-100 hover:text-white transition-colors text-sm">
                <div className="size-6 bg-white/20 rounded-full flex items-center justify-center">
                  <Mail className="size-3" />
                </div>
                Shoot us an email
              </button>
            </div>
          </div>

          {/* Call Support */}
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              Call us
            </h3>
            <div className="flex items-center gap-2 text-white font-medium text-sm">
              <div className="size-6 bg-white/20 rounded-full flex items-center justify-center">
                <Phone className="size-3" />
              </div>
              +91 (555) 123-4567
            </div>
          </div>

          {/* Visit Us */}
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2">
              <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
              Visit us
            </h3>
            <div className="flex flex-col gap-2">
              <div className="flex items-start gap-2 text-blue-100">
                <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">Mumbai Office</p>
                  <p className="text-xs">123 Business Park, Andheri West, Mumbai 400053</p>
                </div>
              </div>
              <div className="flex items-start gap-2 text-blue-100">
                <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">Delhi Office</p>
                  <p className="text-xs">456 Tech Hub, Gurgaon, Haryana 122001</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Onboarding Form */}
      <div className="w-full lg:col-span-3 bg-white dark:bg-slate-800 flex flex-col h-full min-h-0 relative">
        {/* Progress Header - Fixed */}
        <div className="border-b border-slate-200 dark:border-slate-700 p-4 lg:p-6 flex-shrink-0">
          {/* Mobile Logo - Only visible on small screens */}
          <button 
            onClick={() => navigate("/")}
            className="lg:hidden flex items-center gap-2 mb-4 hover:opacity-80 transition-opacity"
          >
            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
              <Car className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">Timelyyy</h1>
          </button>

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                <span className="hidden lg:block">Back</span>
              </Button>
              <h2 className="text-xl lg:text-2xl font-bold text-slate-900 dark:text-slate-100">
                Vendor Application
              </h2>
            </div>
          </div>
          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
        <motion.div
              className="bg-gradient-to-r from-blue-600 to-indigo-600 h-2 rounded-full transition-all duration-300"
              initial={{ width: 0 }}
              animate={{ width: `${formProgress}%` }}
            />
          </div>
        </div>

        {/* Scrollable Form Content */}
        <div className="flex-1 overflow-y-auto min-h-0 pb-24 lg:pb-0">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="p-4 lg:p-8"
          >
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">

                {/* Personal Information Section */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Personal Information</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            First Name <span className="text-red-500">*</span>
                          </FormLabel>
                            <FormControl>
                            <div className="relative">
                              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                              <Input
                                placeholder="Enter your first name"
                                className="pl-10 h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                                {...field}
                              />
                          </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            Last Name <span className="text-red-500">*</span>
                          </FormLabel>
                            <FormControl>
                            <div className="relative">
                              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                              <Input
                                placeholder="Enter your last name"
                                className="pl-10 h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                                {...field}
                              />
                          </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                <FormField
                  control={form.control}
                  name="personalEmail"
                  render={({ field }) => (
                    <FormItem>
                        <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Personal Email (Optional)
                      </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                          <Input
                            type="email"
                              placeholder="Enter your personal email"
                              className="pl-10 h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                            {...field}
                          />
                      </div>
                        </FormControl>
                        <FormDescription>
                          This email will be used for personal communications and account recovery.
                        </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                </div>

                <div className="border-t border-slate-200 dark:border-slate-700 my-4" />

                {/* Business Information Section */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Business Information</h3>
                  </div>

                <FormField
                  control={form.control}
                  name="washCenterName"
                  render={({ field }) => (
                    <FormItem>
                        <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Wash Center Name <span className="text-red-500">*</span>
                      </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                          <Input
                              placeholder="Enter your wash center name"
                              className="pl-10 h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                            {...field}
                          />
                      </div>
                        </FormControl>
                        <FormDescription>
                          This will be the name customers see when booking.
                        </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="businessEmail"
                  render={({ field }) => (
                    <FormItem>
                        <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Business Email <span className="text-red-500">*</span>
                      </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                          <Input
                            type="email"
                            placeholder="Enter business email"
                              className="pl-10 h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                            {...field}
                          />
                      </div>
                        </FormControl>
                        <FormDescription>
                          This email will be used for business communications and notifications.
                        </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="opensAt"
                  render={({ field }) => (
                    <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            Opening Time <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                            <div className="relative">
                              <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                              <Input
                                type="time"
                                className="pl-10 h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                                {...field}
                              />
                            </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="closesAt"
                  render={({ field }) => (
                    <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            Closing Time <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                            <div className="relative">
                              <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                              <Input
                                type="time"
                                className="pl-10 h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                                {...field}
                              />
                            </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="ownerPhoneNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            Owner Phone Number <span className="text-red-500">*</span>
                          </FormLabel>
                            <FormControl>
                            <div className="relative">
                              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                              <Input
                                type="tel"
                                placeholder="Enter owner phone number"
                                className="pl-10 h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                                {...field}
                              />
                          </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="washCentrePhoneNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            Wash Center Contact <span className="text-red-500">*</span>
                          </FormLabel>
                            <FormControl>
                            <div className="relative">
                              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                              <Input
                                type="tel"
                                placeholder="Enter wash center phone number"
                                className="pl-10 h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                                {...field}
                              />
                          </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="border-t border-slate-200 dark:border-slate-700 my-4" />

                {/* Address Information Section */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Address Information</h3>
                  </div>
                  <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={getCurrentLocation}
                      disabled={isLoadingLocation}
                      className="ml-auto"
                    >
                      <LocateFixed className="w-4 h-4 mr-2" />
                      {isLoadingLocation ? "Getting Location..." : "Get Current Location"}
                    </Button>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="street1"
                    render={({ field }) => (
                      <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            Street Address <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                            <Input
                              placeholder="Enter street address"
                              className="h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                              {...field}
                            />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="street2"
                    render={({ field }) => (
                      <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            Area/Locality <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                            <Input
                              placeholder="Enter area or locality"
                              className="h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                              {...field}
                            />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <FormField
                      control={form.control}
                      name="pincode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            Pincode <span className="text-red-500">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter pincode"
                              maxLength={6}
                              className="h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            City <span className="text-red-500">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter city"
                              className="h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            State <span className="text-red-500">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter state"
                              className="h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                    <FormField
                      control={form.control}
                    name="location"
                      render={({ field }) => (
                        <FormItem>
                        <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Location/Landmark <span className="text-red-500">*</span>
                          </FormLabel>
                          <FormControl>
                          <Input
                            placeholder="Enter nearby landmark or location"
                            className="h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                            {...field}
                          />
                          </FormControl>
                        <FormDescription>
                          This helps customers find your wash center easily.
                        </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                </div>

                <div className="border-t border-slate-200 dark:border-slate-700 my-4" />

                {/* Legal Documents Section */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center">
                      <FileText className="w-4 h-4 text-orange-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Legal Documents</h3>
                  </div>

                  <div className="flex items-center gap-4 mb-6">
                    <Label htmlFor="hasGst" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Do you have GST registration?
                    </Label>
                    <FormField
                      control={form.control}
                      name="hasGst"
                      render={({ field }) => (
                        <FormItem>
                    <FormControl>
                      <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                        id="hasGst"
                      />
                    </FormControl>
                        </FormItem>
                      )}
                    />
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      {form.watch("hasGst") ? "Yes" : "No"}
                    </span>
                  </div>

                    {form.watch("hasGst") && (
                      <FormField
                        control={form.control}
                        name="gstNumber"
                        render={({ field }) => (
                          <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                              GST Number <span className="text-red-500">*</span>
                            </FormLabel>
                              <FormControl>
                            <div className="relative">
                              <Receipt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                                <Input
                                placeholder="Enter GST number (e.g., 22AAAAA0000A1Z5)"
                                className="pl-10 h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500 uppercase"
                                  maxLength={15}
                                {...field}
                                onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                              />
                            </div>
                          </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="panNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            PAN Number <span className="text-red-500">*</span>
                          </FormLabel>
                            <FormControl>
                            <div className="relative">
                              <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                              <Input
                                placeholder="Enter PAN number"
                                className="pl-10 h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500 uppercase"
                                maxLength={10}
                                {...field}
                                onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                              />
                          </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="aadharNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            Aadhar Number <span className="text-red-500">*</span>
                          </FormLabel>
                            <FormControl>
                            <div className="relative">
                              <Fingerprint className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                              <Input
                                placeholder="Enter Aadhar number"
                                className="pl-10 h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                                maxLength={12}
                                {...field}
                                onKeyDown={(e) => {
                                  if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                              />
                          </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="panFile"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            PAN Document <span className="text-red-500">*</span>
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type="file"
                                accept=".pdf,application/pdf"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    // Validate file type
                                    if (file.type !== 'application/pdf') {
                                      toast.error('Please upload only PDF files for PAN document');
                                      e.target.value = ''; // Clear the input
                                      field.onChange(undefined);
                                      return;
                                    }
                                    // Validate file size (max 5MB)
                                    if (file.size > 5 * 1024 * 1024) {
                                      toast.error('PAN document size must be less than 5MB');
                                      e.target.value = ''; // Clear the input
                                      field.onChange(undefined);
                                      return;
                                    }
                                    field.onChange(file);
                                    toast.success('PAN document uploaded successfully');
                                  }
                                }}
                                className="h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                              />
                            </div>
                          </FormControl>
                          <FormDescription>
                            Upload PAN card (PDF format only, max 5MB)
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="aadharFile"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            Aadhar Document <span className="text-red-500">*</span>
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type="file"
                                accept=".pdf,application/pdf"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    // Validate file type
                                    if (file.type !== 'application/pdf') {
                                      toast.error('Please upload only PDF files for Aadhar document');
                                      e.target.value = ''; // Clear the input
                                      field.onChange(undefined);
                                      return;
                                    }
                                    // Validate file size (max 5MB)
                                    if (file.size > 5 * 1024 * 1024) {
                                      toast.error('Aadhar document size must be less than 5MB');
                                      e.target.value = ''; // Clear the input
                                      field.onChange(undefined);
                                      return;
                                    }
                                    field.onChange(file);
                                    toast.success('Aadhar document uploaded successfully');
                                  }
                                }}
                                className="h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                          </FormControl>
                          <FormDescription>
                            Upload Aadhar card (PDF format only, max 5MB)
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="border-t border-slate-200 dark:border-slate-700 my-4" />

                {/* Banking Details Section */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center">
                      <Banknote className="w-4 h-4 text-emerald-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Banking Details</h3>
                  </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="bankName"
                        render={({ field }) => (
                          <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                              Bank Name <span className="text-red-500">*</span>
                            </FormLabel>
                            <FormControl>
                            <Input
                              placeholder="Enter bank name"
                              className="h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                              {...field}
                            />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="bankAccountNumber"
                        render={({ field }) => (
                          <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                              Account Number <span className="text-red-500">*</span>
                            </FormLabel>
                              <FormControl>
                            <div className="relative">
                              <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                              <Input
                                placeholder="Enter account number"
                                className="pl-10 h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                                {...field}
                              />
                            </div>
                          </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="ifscCode"
                        render={({ field }) => (
                          <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            IFSC Code <span className="text-red-500">*</span>
                          </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Enter IFSC code"
                              className="h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500 uppercase"
                              maxLength={11}
                              {...field}
                              onChange={(e) => {
                                const normalizedIfsc = normalizeIfscCode(e.target.value);
                                field.onChange(normalizedIfsc);
                                if (normalizedIfsc.length === 11) {
                                  fetchBranchName(normalizedIfsc);
                                }
                              }}
                              />
                            </FormControl>
                            <FormDescription>
                              The 5th character is always the number 0. We correct O to 0 automatically.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="accountType"
                        render={({ field }) => (
                          <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                              Account Type <span className="text-red-500">*</span>
                            </FormLabel>
                            <FormControl>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <SelectTrigger className="h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500">
                                <SelectValue placeholder="Select account type" />
                                </SelectTrigger>
                                <SelectContent>
                                <SelectItem value="savings">Savings</SelectItem>
                                <SelectItem value="current">Current</SelectItem>
                                <SelectItem value="salary">Salary</SelectItem>
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                  </div>

                      <FormField
                        control={form.control}
                        name="branchName"
                        render={({ field }) => (
                          <FormItem>
                        <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Branch Name <span className="text-red-500">*</span>
                        </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Branch name will be auto-filled"
                            className="h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                                readOnly
                            {...field}
                              />
                            </FormControl>
                        <FormDescription>
                          Branch name will be automatically fetched from IFSC code.
                        </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                  {/* Secondary Account Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <Label htmlFor="hasSecondaryAccount" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Do you have a secondary bank account?
                      </Label>
                      <FormField
                        control={form.control}
                        name="hasSecondaryAccount"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                id="hasSecondaryAccount"
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        {form.watch("hasSecondaryAccount") ? "Yes" : "No"}
                      </span>
                    </div>

                    {form.watch("hasSecondaryAccount") && (
                      <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">Secondary Bank Account Details</h4>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="secondaryBankName"
                        render={({ field }) => (
                          <FormItem>
                                <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                  Bank Name <span className="text-red-500">*</span>
                            </FormLabel>
                            <FormControl>
                                  <Input
                                    placeholder="Enter bank name"
                                    className="h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                                    {...field}
                                  />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="secondaryBankAccountNumber"
                        render={({ field }) => (
                          <FormItem>
                                <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                  Account Number <span className="text-red-500">*</span>
                            </FormLabel>
                              <FormControl>
                                  <div className="relative">
                                    <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                                    <Input
                                      placeholder="Enter account number"
                                      className="pl-10 h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                                      {...field}
                                    />
                            </div>
                                </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="secondaryIfscCode"
                        render={({ field }) => (
                          <FormItem>
                                <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                  IFSC Code <span className="text-red-500">*</span>
                                </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Enter IFSC code"
                                    className="h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500 uppercase"
                                    maxLength={11}
                                    {...field}
                                    onChange={(e) => {
                                      const normalizedIfsc = normalizeIfscCode(e.target.value);
                                      field.onChange(normalizedIfsc);
                                      if (normalizedIfsc.length === 11) {
                                        fetchSecondaryBranchName(normalizedIfsc);
                                      }
                                    }}
                              />
                            </FormControl>
                            <FormDescription>
                              The 5th character is always the number 0. We correct O to 0 automatically.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="secondaryAccountType"
                        render={({ field }) => (
                          <FormItem>
                                <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                              Account Type <span className="text-red-500">*</span>
                            </FormLabel>
                            <FormControl>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <SelectTrigger className="h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500">
                                      <SelectValue placeholder="Select account type" />
                                </SelectTrigger>
                                <SelectContent>
                                      <SelectItem value="savings">Savings</SelectItem>
                                      <SelectItem value="current">Current</SelectItem>
                                      <SelectItem value="salary">Salary</SelectItem>
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                        </div>

                      <FormField
                        control={form.control}
                        name="secondaryBranchName"
                        render={({ field }) => (
                          <FormItem>
                              <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                Branch Name <span className="text-red-500">*</span>
                              </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Branch name will be auto-filled"
                                  className="h-11 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-blue-500"
                                readOnly
                                  {...field}
                              />
                            </FormControl>
                              <FormDescription>
                                Branch name will be automatically fetched from IFSC code.
                              </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    )}
                  </div>
                </div>

              </form>
            </Form>
          </motion.div>
        </div>

        {/* Fixed Submit Button Footer */}
        <div className="fixed lg:relative bottom-0 left-0 right-0 lg:bottom-auto lg:left-auto lg:right-auto border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 lg:p-6 flex-shrink-0 z-20 shadow-lg lg:shadow-none">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 max-w-full">
            <div className="text-sm text-slate-500 dark:text-slate-400 hidden sm:block">
              All fields marked with * are required
            </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
              onClick={form.handleSubmit(onSubmit)}
              className="w-full sm:w-auto px-6 lg:px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-lg shadow-lg transition-all duration-200"
                  >
                    {isSubmitting ? (
                      <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting Application...
                      </>
                    ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Submit Application
                </>
                    )}
                  </Button>
                </div>
      </div>
      </div>
      
      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md [&>button]:hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              Onboarding Successful!
            </DialogTitle>
            <DialogDescription className="text-center py-4">
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-slate-900 mb-2">
                    Welcome to Timelyyy!
                  </p>
                  <p className="text-slate-600">
                    Your vendor application has been submitted successfully. 
                    Our team will review your application and get back to you within 24-48 hours.
                  </p>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Button 
              onClick={() => {
                setShowSuccessModal(false);
                navigate("/");
              }}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              Continue to Home Page
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                setShowSuccessModal(false);
                form.reset();
                setFormProgress(0);
              }}
              className="w-full"
            >
              Submit Another Application
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Toaster position="top-center" />
    </div>
  );
};


export default VendorOnboardForm;
